import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { MailIcon, DeleteIcon } from '../../assets/svg/iconos';
import { setMenuLateral } from '../../actions/ui';
import { startAnularVenta } from '../../actions/ventas';
import { startEnviarListadoFacturacion } from '../../actions/listados';
import { DropdownFilter } from '../../components/forms/DropdownFilter';
import { fetchConToken } from '../../helpers/fetch';
import { filtrarVentasFacturables, componerFilaAnulacion } from '../../helpers/ventasFacturacion';
import {
    SIN_SUCURSAL,
    TIPO_VENTA_TODOS,
    TIPO_VENTA_SERVICIOS,
    TIPO_VENTA_PRODUCTOS,
    TIPO_VENTA_MIXTAS,
    construirFiltrosListadoFacturacion,
    filtrarVentasTablaFacturacion,
} from '../../helpers/listadoFacturacion';
import { useEnvioListado } from '../../hooks/useEnvioListado';
import { useClickFueraDelCalendario } from '../../hooks/useClickFueraDelCalendario';
import Calendar from 'react-calendar';
import {
    serviciosVentaParaUI,
    variantesVentaParaUI,
    productosVentaParaUI,
    serviciosCitaParaUI,
    variantesCitaParaUI,
} from '../../helpers/citaVentaSnapshots';

const METODO_ANULACION_VENTA = 'Anulación venta';
const METODO_ANULACION_CITA = 'Anulación cita';
const ETIQUETA_ENVIAR_LISTADO = 'Enviar por email';
const ETIQUETA_ENVIANDO_LISTADO = 'Enviando listado por email…';
// Los rangos amplios tardan: la carga de la tabla tiene el mismo margen que el envío del listado.
const TIMEOUT_CARGA_VENTAS_MS = 300000;

const escapeHtml = (texto) => {
    if (texto == null) return '';
    return String(texto)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

export const Ventas = () => {
    const dispatch = useDispatch();
    const { empresa } = useSelector(state => state.auth);
    const { colores, miniLateral } = useSelector(state => state.ui);
    const { sucursales } = useSelector(state => state.sucursales);
    const { profesionales } = useSelector(state => state.profesionales);
    const catalogoServicios = useSelector(state => state.servicios?.servicios || []);
    const catalogoVariantes = useSelector(state => state.variantes?.variantes || []);
    const catalogoProductos = useSelector(state => state.productos?.productos || []);

    const lineasCatalogoParaVenta = (venta) => {
        const serviciosDesdeVenta = serviciosVentaParaUI(venta, catalogoServicios);
        const serviciosMostrar =
            serviciosDesdeVenta.length > 0
                ? serviciosDesdeVenta
                : venta.cita
                  ? serviciosCitaParaUI(venta.cita, catalogoServicios)
                  : [];
        const variantesDesdeVenta = variantesVentaParaUI(venta, catalogoVariantes);
        const esAnulacion = venta.metodoPago === METODO_ANULACION_CITA || venta.metodoPago === METODO_ANULACION_VENTA;
        const variantesMostrar =
            variantesDesdeVenta.length > 0
                ? variantesDesdeVenta
                : (!esAnulacion && venta.cita)
                  ? variantesCitaParaUI(venta.cita, catalogoVariantes)
                  : [];
        const productosMostrar = productosVentaParaUI(venta, catalogoProductos);
        return { serviciosMostrar, variantesMostrar, productosMostrar };
    };

    // Estados
    const [ventas, setVentas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalFacturado, setTotalFacturado] = useState(0);
    const [totalServicios, setTotalServicios] = useState(0);
    const [totalDescuentos, setTotalDescuentos] = useState(0);
    const [totalProductos, setTotalProductos] = useState(0);
    const [filtroTipoVenta, setFiltroTipoVenta] = useState(TIPO_VENTA_TODOS);

    // Estados para filtros
    const [dateRange, setDateRange] = useState({
        start: moment().subtract(7, 'days').startOf('day'),
        end: moment().endOf('day')
    });
    const [timeRange, setTimeRange] = useState({
        start: '08:30',
        end: '20:30'
    });
    const [selectedSucursal, setSelectedSucursal] = useState([]);
    const [selectedProfesional, setSelectedProfesional] = useState([]);

    // Estado para controlar qué dropdown está abierto
    const [activeDropdown, setActiveDropdown] = useState(null);

    // Estado para los filtros seleccionados
    const [selectedFilters, setSelectedFilters] = useState({
        servicio: [],
        descuento: [],
        variante: [],
        metodoPago: [],
        producto: []
    });

    const [activeTopDropdown, setActiveTopDropdown] = useState(null);

    const [calendarVisibility, setCalendarVisibility] = useState({
        start: false,
        end: false
    });

    // Crear refs separadas para cada calendario
    const startCalendarRef = useRef(null);
    const endCalendarRef = useRef(null);

    useEffect(() => {
        dispatch(setMenuLateral('ventas'));
    }, [dispatch]);

    // Función para obtener las ventas
    const fetchVentas = async (filters = {}) => {
        try {			

            setIsLoading(true);

            const resp = await fetchConToken(`ventas/empresa/${empresa}`, {
                fechaInicio: filters.dateRange?.start || dateRange.start,
                fechaFin: filters.dateRange?.end || dateRange.end,                    
                sucursales: filters.sucursales || selectedSucursal.map(s => s._id),
                profesionales: filters.profesionales || selectedProfesional.map(p => p._id)
            }, 'POST', TIMEOUT_CARGA_VENTAS_MS);
			
            const body = await resp.json();

            if (body.ok) {
                const ventasFacturables = filtrarVentasFacturables(body.ventas);
                setVentas(ventasFacturables);
                setSelectedFilters({
                    servicio: [],
                    descuento: [],
                    variante: [],
                    metodoPago: [],
                    producto: []
                });
            } else {
                throw new Error(body.msg || 'Error al obtener las ventas');
            }
        } catch (error) {
            console.error('Error:', error);
            // Aquí podrías mostrar una notificación de error
        } finally {
            setIsLoading(false);
        }
    };

    // Efecto inicial para cargar las ventas y configurar el menú lateral
    useEffect(() => {
        dispatch(setMenuLateral('ventas'));		
        if (empresa) {
            fetchVentas();
        }
    }, [empresa, dispatch]);

    // Actualizamos los handlers de los filtros para usar fetchVentas
    const handleDateRangeChange = async (newRange) => {
        setDateRange(newRange);
        await fetchVentas({ dateRange: newRange });
    };

    const handleTimeRangeChange = (newTimeRange) => {
        setTimeRange(newTimeRange);
    };

    const handleSucursalChange = async (selected) => {

		const sucursalesSelected = [
            ...sucursales.filter(s => selected.includes(s._id)),
            ...(selected.includes(SIN_SUCURSAL) ? [{ _id: SIN_SUCURSAL, nombre: 'Sin sucursal' }] : [])
        ];

        setSelectedSucursal(sucursalesSelected);
        setActiveTopDropdown(null);
        await fetchVentas({
            sucursales: sucursalesSelected.map(s => s._id)
        });
    };

    const handleProfesionalChange = async (selected) => {
        
        const profesionalesSelected = profesionales.filter(p => selected.includes(p._id));
        
        setSelectedProfesional(profesionalesSelected);
        setActiveTopDropdown(null);
        await fetchVentas({
            profesionales: profesionalesSelected.map(p => p._id)
        });
    };

    const filtrosListado = useMemo(
        () => construirFiltrosListadoFacturacion({
            dateRange,
            timeRange,
            selectedSucursal,
            selectedProfesional,
            selectedFilters,
            filtroTipoVenta,
        }),
        [dateRange, timeRange, selectedSucursal, selectedProfesional, selectedFilters, filtroTipoVenta]
    );

    // Un único filtrado para la tabla y el listado por email: la consulta ya acota sucursal y
    // profesional; aquí se aplican periodo (por fechaVenta), franja, cabeceras y tipo de venta.
    const filteredVentas = useMemo(
        () => filtrarVentasTablaFacturacion(ventas, filtrosListado, lineasCatalogoParaVenta),
        [ventas, filtrosListado, catalogoServicios, catalogoVariantes, catalogoProductos]
    );

    const { enviandoListado, enviarListado } = useEnvioListado(
        () => startEnviarListadoFacturacion(filtrosListado)
    );
    // Durante una recarga los filtros de pantalla aún no corresponden a las filas que se ven.
    const envioListadoBloqueado = enviandoListado || isLoading;
    const etiquetaEnvioListado = enviandoListado ? ETIQUETA_ENVIANDO_LISTADO : ETIQUETA_ENVIAR_LISTADO;

    // Actualizar totales cuando cambian las ventas filtradas
    useEffect(() => {
        const sumaServicios = filteredVentas.reduce((sum, venta) => sum + (venta.importeServicios || 0), 0);
        const sumaDescuentos = filteredVentas.reduce((sum, venta) => sum + (venta.importeDescuentos || 0), 0);
        const sumaProductos = filteredVentas.reduce((sum, venta) => sum + (venta.importeProductos || 0), 0);
        const sumaImportes = filteredVentas.reduce((sum, venta) => sum + (venta.importe || 0), 0);
        setTotalServicios(sumaServicios);
        setTotalDescuentos(sumaDescuentos);
        setTotalProductos(sumaProductos);
        setTotalFacturado(sumaImportes);
    }, [filteredVentas]);

    // Función para obtener opciones únicas del array de ventas
    const getUniqueOptions = (field) => {
        const options = new Set();
        
        ventas.forEach(venta => {
            const { serviciosMostrar, variantesMostrar, productosMostrar } = lineasCatalogoParaVenta(venta);
            if (field === 'servicio') {
                serviciosMostrar.forEach((lineaServicio) => {
                    if (lineaServicio?.nombre) options.add(lineaServicio.nombre);
                });
            } else if (field === 'descuento') {
                venta.promociones?.forEach((promo) => options.add(promo.titulo));
            } else if (field === 'variante') {
                variantesMostrar.forEach((lineaVariante) => {
                    if (lineaVariante?.nombre) options.add(lineaVariante.nombre);
                });
            } else if (field === 'metodoPago') {
                options.add(venta.metodoPago);
            } else if (field === 'producto') {
                productosMostrar.forEach((lineaProducto) => {
                    const titulo = lineaProducto?.titulo || lineaProducto?.nombre;
                    if (titulo) options.add(titulo);
                });
            }
        });

        return Array.from(options).map(option => ({
            id: option,
            label: option
        }));
    };

    // Función para obtener opciones de los estados de Redux
    const getOptions = (field) => {
        if (field === 'sucursal') {
            return sucursales.map(sucursal => ({
                id: sucursal._id,
                label: sucursal.nombre
            }));
        } else if (field === 'profesional') {
            return profesionales.map(profesional => ({
                id: profesional._id,
                label: profesional.nombre
            }));
        }
        
        // Para el resto de campos usamos el método existente
        return getUniqueOptions(field);
    };

    // Función para manejar el click en las cabeceras
    const handleHeaderClick = (columnName) => {
        if (activeDropdown === columnName) {
            setActiveDropdown(null);
        } else {
            setActiveDropdown(columnName);
        }
    };

    // Función para manejar la selección de filtros
    const handleFilterSelect = (columnName, selectedOptions) => {
        setSelectedFilters(prev => ({
            ...prev,
            [columnName]: selectedOptions
        }));
        setActiveDropdown(null);
    };

    // Función para obtener el texto a mostrar en los filtros superiores
    const getDisplayText = (selected, type) => {
        if (!selected || selected.length === 0) {
            return type === 'sucursal' ? 'Todas las sucursales' : 'Todos los profesionales';
        }
        if (selected.length === 1) {
            return selected[0].nombre;
        }
        return `${selected.length} seleccionados`;
    };

    // Manejador para abrir/cerrar los dropdowns superiores
    const handleTopFilterClick = (filterType) => {
        setActiveTopDropdown(activeTopDropdown === filterType ? null : filterType);
    };

    // Función para formatear la fecha para mostrar (DD/MM/YYYY)
    const formatDateForDisplay = (momentDate) => {
        return momentDate.format('DD/MM/YYYY');
    };

    // Usar el hook para cada calendario
    useClickFueraDelCalendario(startCalendarRef, () => {
        setCalendarVisibility(prev => ({ ...prev, start: false }));
    }, calendarVisibility.start);

    useClickFueraDelCalendario(endCalendarRef, () => {
        setCalendarVisibility(prev => ({ ...prev, end: false }));
    }, calendarVisibility.end);


    // Handler para mostrar/ocultar calendario
    const toggleCalendar = (type) => {
        setCalendarVisibility(prev => ({
            start: type === 'start' ? !prev.start : false,
            end: type === 'end' ? !prev.end : false
        }));
    };

    // Presets de fecha
    const [activePreset, setActivePreset] = useState('ultimos7');
    const datePresets = [
        { key: 'hoy', label: 'Hoy', getRange: () => ({ start: moment().startOf('day'), end: moment().endOf('day') }) },
        { key: 'ayer', label: 'Ayer', getRange: () => ({ start: moment().subtract(1, 'day').startOf('day'), end: moment().subtract(1, 'day').endOf('day') }) },
        { key: 'estaSemana', label: 'Esta semana', getRange: () => ({ start: moment().startOf('isoWeek'), end: moment().endOf('day') }) },
        { key: 'ultimaSemana', label: 'Última semana', getRange: () => ({ start: moment().subtract(1, 'week').startOf('isoWeek'), end: moment().subtract(1, 'week').endOf('isoWeek') }) },
        { key: 'ultimos7', label: 'Últimos 7 días', getRange: () => ({ start: moment().subtract(7, 'days').startOf('day'), end: moment().endOf('day') }) },
        { key: 'esteMes', label: 'Este mes', getRange: () => ({ start: moment().startOf('month'), end: moment().endOf('day') }) },
        { key: 'ultimoMes', label: 'Último mes', getRange: () => ({ start: moment().subtract(1, 'month').startOf('month'), end: moment().subtract(1, 'month').endOf('month') }) },
        { key: 'ultimos30', label: 'Últimos 30 días', getRange: () => ({ start: moment().subtract(30, 'days').startOf('day'), end: moment().endOf('day') }) },
    ];

    const handlePresetClick = (preset) => {
        setActivePreset(preset.key);
        const range = preset.getRange();
        handleDateRangeChange(range);
    };

    // Handler para cambio de fecha
    const handleDateChange = (date, type) => {
        const newDate = moment(date);
        setActivePreset(null);
        handleDateRangeChange({
            ...dateRange,
            [type]: type === 'end' ? newDate.endOf('day') : newDate.startOf('day')
        });
        setCalendarVisibility({ start: false, end: false });
    };

    const esRegistroAnulacion = (venta) => {
        const metodoPago = venta?.metodoPago;
        return metodoPago === METODO_ANULACION_VENTA || metodoPago === METODO_ANULACION_CITA;
    };

    const esVentaAnulada = (venta) => venta?.anulada === true || esRegistroAnulacion(venta);

    const esVentaAnulable = (venta) => !esVentaAnulada(venta);

    const buildResumenAnulacionHtml = (venta) => {
        const clienteNombre = `${venta.usuario?.nombre || ''} ${venta.usuario?.apellidos || ''}`.trim() || '-';
        const importeFormateado = `${(venta.importe || 0).toFixed(2)}€`;
        const fechaVentaFormateada = venta.fechaVenta
            ? moment(venta.fechaVenta).format('DD/MM/YYYY HH:mm')
            : '-';
        const { serviciosMostrar, variantesMostrar, productosMostrar } = lineasCatalogoParaVenta(venta);
        const lineasResumen = [
            ...serviciosMostrar.map((linea) => linea?.nombre).filter(Boolean),
            ...variantesMostrar.map((linea) => linea?.nombre).filter(Boolean),
            ...productosMostrar.map((linea) => linea?.titulo || linea?.nombre).filter(Boolean),
        ];
        const resumenLineasHtml = lineasResumen.length > 0
            ? `<div class="anular-resumen-lineas">${escapeHtml(lineasResumen.join(', '))}</div>`
            : '';

        return `
            <div class="anular-venta-modal">
                <div class="anular-venta-detalle">
                    <div><strong>Cliente:</strong> ${escapeHtml(clienteNombre)}</div>
                    <div><strong>Importe:</strong> ${escapeHtml(importeFormateado)}</div>
                    <div><strong>Fecha venta:</strong> ${escapeHtml(fechaVentaFormateada)}</div>
                    ${resumenLineasHtml}
                </div>
                <label class="anular-venta-checkbox">
                    <input type="checkbox" id="anular-venta-reembolso" />
                    <span>Reembolsar el importe al monedero del cliente</span>
                </label>
            </div>
        `;
    };

    const handleAnularVenta = async (venta) => {
        if (!esVentaAnulable(venta)) return;

        const { isConfirmed, value } = await Swal.fire({
            title: 'Anular venta',
            html: buildResumenAnulacionHtml(venta),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Anular venta',
            cancelButtonText: 'Cancelar',
            focusCancel: true,
            preConfirm: () => {
                const checkbox = document.getElementById('anular-venta-reembolso');
                return { reembolsarMonedero: Boolean(checkbox && checkbox.checked) };
            },
        });

        if (!isConfirmed) return;

        const reembolsarMonedero = Boolean(value?.reembolsarMonedero);

        Swal.fire({
            title: 'Anulando venta...',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        const resp = await dispatch(startAnularVenta(venta._id, { reembolsarMonedero }));

        if (!resp || !resp.ok) {
            await Swal.fire({
                title: 'Error',
                text: resp?.msg || 'No se ha podido anular la venta',
                icon: 'error',
                confirmButtonText: 'Cerrar',
            });
            return;
        }

        // EX-572: actualización local optimista en lugar de re-consultar todo el rango de fechas
        // (que con rangos amplios disparaba el tiempo de espera y, además, reseteaba los filtros
        // de cabecera). El backend devuelve la venta de anulación ya con su desglose; se compone
        // la fila del contra-asiento heredando los datos ya poblados de la venta original
        // (cliente, profesional, sucursal, líneas) y se marca la original como anulada. Los
        // totales se recalculan solos y los filtros seleccionados se conservan.
        const ventaAnulacion = resp.venta;
        if (ventaAnulacion) {
            const filaAnulacion = componerFilaAnulacion(venta, ventaAnulacion);
            setVentas((ventasActuales) => [
                filaAnulacion,
                ...ventasActuales.map((ventaActual) =>
                    ventaActual._id === venta._id
                        ? { ...ventaActual, anulada: true }
                        : ventaActual
                ),
            ]);
        } else {
            // Fallback defensivo: si el backend no devolviera la venta de anulación, recargar.
            await fetchVentas();
        }

        await Swal.fire({
            title: 'Venta anulada',
            text: reembolsarMonedero
                ? 'La venta se ha anulado y el importe se ha reembolsado al monedero del cliente.'
                : 'La venta se ha anulado correctamente.',
            icon: 'success',
            confirmButtonText: 'Aceptar',
            timer: 2500,
        });
    };

    return (
        <div 
            className="ventas-screen"
            style={{
                '--colorPrincipal': colores.colorPrincipal,
                '--colorSecundario': colores.colorSecundario,
                '--color3': colores.color3,
                '--color4': colores.color4,
                '--color5': colores.color5,
                '--color6': colores.color6,
                '--color7': colores.color7,
            }}
        >
            <div className={`preheader ${!miniLateral && 'compress'}`}>
                <div className="title">
                    <h1 style={{color: colores.colorPrincipal}}>Facturación y estadísticas</h1>
                </div>
                <div className="botonera">
                    {filteredVentas.length > 0 && (
                        <button
                            type="button"
                            className={`btn-enviar-listado${enviandoListado ? ' enviando' : ''}`}
                            style={{ backgroundColor: colores.color6, color: colores.colorPrincipal }}
                            title={etiquetaEnvioListado}
                            aria-label={etiquetaEnvioListado}
                            aria-busy={enviandoListado}
                            disabled={envioListadoBloqueado}
                            onClick={enviarListado}
                        >
                            <MailIcon color={colores.colorPrincipal} />
                            <span>{etiquetaEnvioListado}</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="date-presets">
                {datePresets.map(preset => (
                    <button
                        key={preset.key}
                        className={`preset-btn${activePreset === preset.key ? ' active' : ''}`}
                        style={activePreset === preset.key ? { backgroundColor: colores.colorPrincipal, color: 'white' } : {}}
                        onClick={() => handlePresetClick(preset)}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>

            <div className="filters">
                <div className="filter-group">
                    <label>FECHAS / PERIODO</label>
                    <div className="date-range">
                        <div className="date-input">
                            <div 
                                className="date-display"
                                onClick={() => toggleCalendar('start')}
                            >
                                {formatDateForDisplay(dateRange.start)}
                            </div>
                            <div
                                className={`calendar-dropdown ${calendarVisibility.start ? 'active' : ''}`}
                                ref={startCalendarRef}
                            >
                                <Calendar
                                    onChange={(date) => handleDateChange(date, 'start')}
                                    value={dateRange.start.toDate()}
                                    locale="es-ES"
                                />
                            </div>
                        </div>
                        <span>-</span>
                        <div className="date-input">
                            <div 
                                className="date-display"
                                onClick={() => toggleCalendar('end')}
                            >
                                {formatDateForDisplay(dateRange.end)}
                            </div>
                            <div
                                className={`calendar-dropdown ${calendarVisibility.end ? 'active' : ''}`}
                                ref={endCalendarRef}
                            >
                                <Calendar
                                    onChange={(date) => handleDateChange(date, 'end')}
                                    value={dateRange.end.toDate()}
                                    locale="es-ES"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="filter-group">
                    <label>JORNADA</label>
                    <div className="filter-header jornada-header">
                        <input
                            type="time"
                            value={timeRange.start}
                            onChange={(e) => handleTimeRangeChange({
                                ...timeRange,
                                start: e.target.value
                            })}
                        />
                        <span>-</span>
                        <input
                            type="time"
                            value={timeRange.end}
                            onChange={(e) => handleTimeRangeChange({
                                ...timeRange,
                                end: e.target.value
                            })}
                        />
                    </div>
                </div>

                <div className="filter-group">
                    <label>SUCURSAL</label>
                    <div 
                        className="filter-header"
                        onClick={() => handleTopFilterClick('sucursal')}
                    >
                        <span>{getDisplayText(selectedSucursal, 'sucursal')}</span>
                        
                    </div>
                    {activeTopDropdown === 'sucursal' && (
                        <div className="filter-dropdown">
                            <DropdownFilter
                                options={[
                                    { id: SIN_SUCURSAL, label: 'Sin sucursal', checked: selectedSucursal.some(selected => selected._id === SIN_SUCURSAL) },
                                    ...sucursales.map(s => ({
                                        id: s._id,
                                        label: s.nombre,
                                        checked: selectedSucursal.some(selected => selected._id === s._id)
                                    }))
                                ]}
                                selected={selectedSucursal.map(s => s._id)}
                                onChange={handleSucursalChange}
                            />
                        </div>
                    )}
                </div>

                <div className="filter-group">
                    <label>PROFESIONAL</label>
                    <div 
                        className="filter-header"
                        onClick={() => handleTopFilterClick('profesional')}
                    >
                        <span>{getDisplayText(selectedProfesional, 'profesional')}</span>
                        
                    </div>
                    {activeTopDropdown === 'profesional' && (
                        <div className="filter-dropdown">
                            <DropdownFilter
                                options={profesionales.map(p => ({
                                    id: p._id,
                                    label: p.nombre,
                                    checked: selectedProfesional.some(selected => selected._id === p._id)
                                }))}
                                selected={selectedProfesional.map(p => p._id)}
                                onChange={handleProfesionalChange}
                            />
                        </div>
                    )}
                </div>

                <div className="filter-group">
                    <label>TIPO DE VENTA</label>
                    <div className="filter-header">
                        <select
                            className="tipo-venta-select"
                            value={filtroTipoVenta}
                            onChange={(e) => setFiltroTipoVenta(e.target.value)}
                        >
                            <option value={TIPO_VENTA_TODOS}>Todos</option>
                            <option value={TIPO_VENTA_SERVICIOS}>Solo servicios</option>
                            <option value={TIPO_VENTA_PRODUCTOS}>Solo productos</option>
                            <option value={TIPO_VENTA_MIXTAS}>Mixtas</option>
                        </select>
                    </div>
                </div>

                <div className="filter-group total-facturado">
                    <label>IMPORTE FACTURADO</label>
                    <div className="total-amount">
                        {totalFacturado.toFixed(2)}€
                    </div>
                    <div className="total-desglose">
                        <span className="desglose-item">Servicios: {totalServicios.toFixed(2)}€</span>
                        <span className="desglose-item">Descuentos: -{totalDescuentos.toFixed(2)}€</span>
                        <span className="desglose-item">Productos: {totalProductos.toFixed(2)}€</span>
                    </div>
                </div>
            </div>

            <div className="table-container">
                <div className="table-ventas">
                    <div className='table-row headers-row'>
                    <div className='column cliente'>
                        <div className='column-header'>
                            <p>Cliente</p>
                        </div>
                    </div>

                    <div className='column servicio'>
                        <div 
                            className='column-header'
                            onClick={() => handleHeaderClick('servicio')}
                        >
                            <p>Servicio</p>
                            
                        </div>
                        {activeDropdown === 'servicio' && (
                            <div className="filter-dropdown">
                                <DropdownFilter
                                    options={getOptions('servicio')}
                                    selected={selectedFilters.servicio}
                                    onChange={(selected) => handleFilterSelect('servicio', selected)}
                                />
                            </div>
                        )}
                    </div>

                    <div className='column descuento'>
                        <div 
                            className='column-header'
                            onClick={() => handleHeaderClick('descuento')}
                        >
                            <p>Descuento</p>
                            
                        </div>
                        {activeDropdown === 'descuento' && (
                            <div className="filter-dropdown">
                                <DropdownFilter
                                    options={getOptions('descuento')}
                                    selected={selectedFilters.descuento}
                                    onChange={(selected) => handleFilterSelect('descuento', selected)}
                                />
                            </div>
                        )}
                    </div>

                    <div className='column variante'>
                        <div 
                            className='column-header'
                            onClick={() => handleHeaderClick('variante')}
                        >
                            <p>Variante</p>
                            
                        </div>
                        {activeDropdown === 'variante' && (
                            <div className="filter-dropdown">
                                <DropdownFilter
                                    options={getOptions('variante')}
                                    selected={selectedFilters.variante}
                                    onChange={(selected) => handleFilterSelect('variante', selected)}
                                />
                            </div>
                        )}
                    </div>

                    <div className='column producto'>
                        <div 
                            className='column-header'
                            onClick={() => handleHeaderClick('producto')}
                        >
                            <p>Producto</p>
                            
                        </div>
                        {activeDropdown === 'producto' && (
                            <div className="filter-dropdown">
                                <DropdownFilter
                                    options={getOptions('producto')}
                                    selected={selectedFilters.producto}
                                    onChange={(selected) => handleFilterSelect('producto', selected)}
                                />
                            </div>
                        )}
                    </div>

                    <div className='column metodoPago'>
                        <div 
                            className='column-header'
                            onClick={() => handleHeaderClick('metodoPago')}
                        >
                            <p>Método de pago</p>
                            
                        </div>
                        {activeDropdown === 'metodoPago' && (
                            <div className="filter-dropdown">
                                <DropdownFilter
                                    options={getOptions('metodoPago')}
                                    selected={selectedFilters.metodoPago}
                                    onChange={(selected) => handleFilterSelect('metodoPago', selected)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="column importeServicios">
                        <div className="column-header">
                            <p>Imp. Servicios</p>
                        </div>
                    </div>

                    <div className="column importeDescuentos">
                        <div className="column-header">
                            <p>Imp. Descuento</p>
                        </div>
                    </div>

                    <div className="column importeProductos">
                        <div className="column-header">
                            <p>Imp. Productos</p>
                        </div>
                    </div>

                    <div className="column importe">
                        <div className="column-header">
                            <p>Importe</p>
                        </div>
                    </div>

                    <div className="column fechaVenta">
                        <div className="column-header">
                            <p>Fecha Venta</p>
                        </div>
                    </div>

                    <div className="column fechaCita">
                        <div className="column-header">
                            <p>Fecha Cita</p>
                        </div>
                    </div>

                    <div className="column fechaAnulacion">
                        <div className="column-header">
                            <p>Fecha Anulación</p>
                        </div>
                    </div>

                    <div className="column profesionalNombre">
                        <div className="column-header">
                            <p>Profesional</p>
                        </div>
                    </div>

                    <div className="column seleccionAleatoria">
                        <div className="column-header">
                            <p>Sel. Profesional</p>
                        </div>
                    </div>

                    <div className="column anular">
                        <div className="column-header">
                            <p>Anular</p>
                        </div>
                    </div>
                </div>
                <div className='table-scrollable'>
                    {isLoading ? (
                        <div className="loading-ventas">
                            <div className="bg-loading"></div>
                            <p>Cargando ventas...</p>
                        </div>
                    ) : (
                        filteredVentas.map(venta => {
                            const { serviciosMostrar, variantesMostrar, productosMostrar } =
                                lineasCatalogoParaVenta(venta);
                            return (
                            <div key={venta._id} className={`table-row${esVentaAnulada(venta) ? ' row-anulada' : ''}`}>
                                <div className="column cliente">
                                    {venta.usuario?.nombre} {venta.usuario?.apellidos}
                                </div>
                                <div className="column servicio">
                                    {serviciosMostrar
                                        .map((lineaServicio) => lineaServicio?.nombre)
                                        .filter(Boolean)
                                        .join(', ')}
                                </div>
                                <div className="column descuento">
                                    {venta.promociones?.map(p => p.titulo).join(', ')}
                                </div>
                                <div className="column variante">
                                    {variantesMostrar
                                        .map((lineaVariante) => lineaVariante?.nombre)
                                        .filter(Boolean)
                                        .join(', ')}
                                </div>
                                <div className="column producto">
                                    {productosMostrar
                                        .map((lineaProducto) => lineaProducto?.titulo || lineaProducto?.nombre)
                                        .filter(Boolean)
                                        .join(', ')}
                                </div>
                                <div className="column metodoPago">
                                    {venta.metodoPago}
                                </div>
                                <div className="column importeServicios">
                                    {(venta.importeServicios || 0).toFixed(2)}€
                                </div>
                                <div className="column importeDescuentos">
                                    {(venta.importeDescuentos || 0).toFixed(2)}€
                                </div>
                                <div className="column importeProductos">
                                    {(venta.importeProductos || 0).toFixed(2)}€
                                </div>
                                <div className="column importe">
                                    {venta.importe?.toFixed(2)}€
                                </div>
                                <div className="column fechaVenta">
                                    {venta.fechaVenta ? moment(venta.fechaVenta).format('DD/MM/YYYY HH:mm') : '-'}
                                </div>
                                <div className="column fechaCita">
                                    {venta.fechaCita ? moment(venta.fechaCita).format('DD/MM/YYYY HH:mm') : '-'}
                                </div>
                                <div className="column fechaAnulacion">
                                    {venta.fechaAnulacion != null ? moment(venta.fechaAnulacion).format('DD/MM/YYYY HH:mm') : '-'}
                                </div>
                                <div className="column profesionalNombre">
                                    {venta.profesional?.nombre || '-'}
                                </div>
                                <div className="column seleccionAleatoria">
                                    {venta.seleccionAleatoria === true ? 'Aleatorio' : 'Manual'}
                                </div>
                                <div className="column anular">
                                    {esVentaAnulable(venta) && (
                                        <button
                                            type="button"
                                            className="btn-anular-venta"
                                            title="Anular venta"
                                            aria-label="Anular venta"
                                            onClick={() => handleAnularVenta(venta)}
                                        >
                                            <DeleteIcon color="#ffffff" background={colores.colorPrincipal || '#1A28D7'} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    </div>
    );
}; 